/**
 * The Room board on the web (core roomboard.ts decides everything; this maps a step to where its one action goes and
 * which picture it shows). Pure, so the screens stay thin and the rules are testable.
 */
import type { BoardMoment, RoomBoard, RoomStep, StepIcon } from "@logwave/core";
import type { IconName } from "../components/Icons.tsx";

/** "I have N minutes" choices (0 = no limit). */
export const MINUTE_CHOICES = [5, 10, 15, 30] as const;

export const STEP_ICON: Record<StepIcon, IconName> = {
  alert: "alert", thermometer: "thermo", fridge: "thermo", spray: "spray", restroom: "restroom", pest: "bug", trash: "trash",
  broom: "drop", box: "box", hand: "hands", check: "check", wrench: "wrench", clipboard: "paper",
};

/** The moment chip's safety tone: peak is a heads-up, a lull is go, close is info. */
export const MOMENT_TONE: Record<BoardMoment, "warn" | "go" | "info"> = { before: "info", opening: "info", peak: "warn", steady: "info", slow: "go", close: "info", after: "info" };

/** The in-app place a step's action goes (every action is a link: the board never checks anything off itself). */
export function actionPath(s: RoomStep): string | undefined {
  const { kind, target } = s.action;
  switch (kind) {
    case "log": return `/do?check=${encodeURIComponent(s.id.replace(/^check:/, ""))}`;
    case "object": return `/places/${encodeURIComponent(target.id)}`;
    case "fix": return `/do/fix/${encodeURIComponent(target.id)}`;
    case "order": return "/order";
    case "report":
      if (target.type === "incident") return `/report#${encodeURIComponent(target.id)}`;
      return `/report?type=other&area=${encodeURIComponent(s.room)}&details=${encodeURIComponent(s.what)}`;
    case "nag": return undefined; // the Nag sheet opens in place
  }
}

/** The room to show: the one asked for (resumable from the link), else the first with something to do, else the first. */
export function pickRoom(b: Pick<RoomBoard, "rooms">, wanted?: string | null): string | undefined {
  if (wanted && b.rooms.some((r) => r.name === wanted)) return wanted;
  return (b.rooms.find((r) => r.count > 0) ?? b.rooms[0])?.name;
}

/** ?min= → a budget from the choices, else none. */
export function parseMinutes(v: string | null | undefined): number | undefined {
  const n = Number(v);
  return (MINUTE_CHOICES as readonly number[]).includes(n) ? n : undefined;
}

/** "About 6 min" (or "6 min" once measured). */
export const minutesLabel = (s: Pick<RoomStep, "estMinutes" | "estAbout">) => `${s.estAbout ? "About " : ""}${s.estMinutes} min`;
